import type { ReactElement } from "react";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./DiscountedUnitPrice.module.css";

/**
 * Which price pair is being shown:
 * - `"b2b"` — retailer view: the consumer price is the *recommended* retail price
 *   (not struck, labelled "Recommended price") beside the retailer's own net
 *   price (labelled "Your price"). Captions default to "incl. VAT" / "VAT. 0%".
 * - `"discount"` — consumer discount: the original price struck through beside
 *   the discounted price, with no labels. Captions default to none.
 */
export type DiscountedUnitPriceVariant = "b2b" | "discount";

interface DiscountedUnitPriceProps {
  variant: DiscountedUnitPriceVariant;
  /** Standard (list / RRP) unit price incl. VAT — the consumer reference price. */
  standardInclVat: string | null | undefined;
  /**
   * The emphasised (accent) unit price shown below the standard price: the
   * retailer's net purchase price excl. VAT for B2B, or the discounted price
   * for consumer discounts.
   */
  accentPrice: string | null | undefined;
  /** Append a "/pcs" suffix to each price (per-unit contexts). */
  perPiece?: boolean;
  /**
   * Recurring billing-period suffix (e.g. "/ year") appended to each price.
   * Takes precedence over `perPiece` so subscription lines read as recurring
   * rather than per-piece.
   */
  periodLabel?: string | null;
  /** Visual size: "lg" for the product-detail hero, "sm"/"md" elsewhere. */
  size?: "sm" | "md" | "lg";
  /**
   * Caption after the standard price. Omitted → the variant default ("incl. VAT"
   * for B2B, none for discounts); `null` → no caption; a string overrides it.
   */
  standardLabel?: string | null;
  /**
   * Caption after the accent (net/discounted) price. Omitted → the variant
   * default ("VAT. 0%" for B2B, none for discounts); `null` → no caption; a
   * string overrides it.
   */
  netLabel?: string | null;
  /**
   * Render on a dark surface (e.g. the product-detail page): the standard price
   * switches to light text so it stays legible against a dark background.
   */
  onDark?: boolean;
  /**
   * Card surfaces: place the reference price beside the accent price rather than
   * above it, a step smaller so the price actually charged stays primary.
   */
  inline?: boolean;
  className?: string;
}

/**
 * Reference / accent price breakdown — stacked by default, side by side with
 * `inline` (card surfaces) — shared across the purchase flow
 * (product page, listing cards, quick order, cart, checkout, order history,
 * confirmation).
 *
 * Two uses share the layout, picked by `variant`: (1) `"b2b"` — B2B customers
 * always buy at 0% VAT, so the consumer price incl. VAT is shown as the
 * recommended retail price above the retailer's own net price excl. VAT
 * (accent), each with a label (GROW-367: no strikethrough, it isn't a sale);
 * and (2) `"discount"` — the original price struck through above the
 * discounted price (accent). Renders nothing when no price is available.
 */
export function DiscountedUnitPrice({
  variant,
  standardInclVat,
  accentPrice,
  perPiece = false,
  periodLabel,
  size = "md",
  onDark = false,
  inline = false,
  standardLabel,
  netLabel,
  className,
}: DiscountedUnitPriceProps): ReactElement | null {
  const texts = useLocalizedTexts();

  if (!standardInclVat && !accentPrice) return null;

  // A recurring period label (space-separated, e.g. " / year") wins over the
  // per-piece suffix so subscription lines don't get mislabelled as per-piece.
  const priceSuffix = periodLabel
    ? ` ${periodLabel}`
    : perPiece
      ? t(texts, "Tracker.Cart.PerPiece", "/pcs")
      : "";
  const isB2b = variant === "b2b";
  // A prop of `undefined` falls back to the variant's default caption (B2B
  // shows the VAT basis of each price, discounts show none); `null` hides it.
  const standardCaption =
    standardLabel === undefined
      ? isB2b
        ? t(texts, "Tracker.Products.IncludingVAT", "incl. VAT")
        : null
      : standardLabel;
  const netCaption =
    netLabel === undefined
      ? isB2b
        ? t(texts, "Tracker.Cart.VatZero", "VAT. 0%")
        : null
      : netLabel;
  const standardPrefix = isB2b
    ? t(texts, "B2B.RecommendedPrice", "Recommended price")
    : null;
  const netPrefix = isB2b ? t(texts, "B2B.YourPrice", "Your price") : null;

  const containerClass = [
    styles.container,
    styles[variant],
    styles[size],
    inline && styles.inline,
    onDark && styles.onDark,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      className={containerClass}
      data-component="discounted-unit-price"
      data-variant={variant}
    >
      {standardInclVat && (
        <span className={styles.standard}>
          {standardPrefix && (
            <span className={styles.prefix}>{standardPrefix}</span>
          )}
          {standardInclVat}
          {priceSuffix}
          {standardCaption && (
            <span className={styles.label}>{standardCaption}</span>
          )}
        </span>
      )}
      {accentPrice && (
        <span className={styles.net}>
          {netPrefix && <span className={styles.prefix}>{netPrefix}</span>}
          {accentPrice}
          {priceSuffix}
          {netCaption && <span className={styles.label}>{netCaption}</span>}
        </span>
      )}
    </span>
  );
}

export default DiscountedUnitPrice;
